import { useEffect, RefObject } from 'react';

/**
 * Fecha um painel quando o toque cai fora dele.
 *
 * ── O que estava acontecendo ──────────────────────────────────────────────
 *
 * Três painéis abrem na barra de navegação — o menu do celular, o seletor de
 * cores e o menu do avatar — e cada um resolvia isso de um jeito:
 *
 *   menu do avatar   ouvia `mousedown` no documento          → funcionava
 *   seletor de cores tinha um `<div>` de fundo com `onClick` → NÃO funcionava
 *   menu do celular  não tinha nada                          → NÃO funcionava
 *
 * ── Por que o `<div>` de fundo não funcionava ────────────────────────────
 *
 * Ele é `position: fixed; inset: 0`, o que deveria cobrir a tela toda. Só que
 * `.app-nav` tem `backdrop-filter`, e **backdrop-filter faz do elemento o bloco
 * de contenção dos descendentes `fixed`**. Medido: o fundo saía com 390x51 — a
 * caixa da barra — em vez de 390x844. Ele cobria a própria barra e mais nada,
 * então tocar no meio da tela não fechava coisa alguma.
 *
 * É invisível de ler: o CSS diz `inset: 0` e parece certo. Só medindo a caixa
 * renderizada é que aparece.
 *
 * ── Por que um ouvinte, e não consertar o fundo ──────────────────────────
 *
 * Dava para tirar o fundo do portal ou mudar o `backdrop-filter`. As duas
 * consertam UM painel e deixam a mesma armadilha montada para o próximo, que
 * também vai morar dentro da barra. Um ouvinte no documento não depende de
 * empilhamento, de bloco de contenção nem de onde o elemento mora.
 *
 * ── `pointerdown`, e não `click` ─────────────────────────────────────────
 *
 * `pointerdown` cobre mouse, toque e caneta num evento só, e dispara no
 * começo do gesto — fechar no `click` deixa o painel aberto durante o arrasto,
 * e no celular um toque que rola a tela nem vira `click`.
 */
type Alvo = RefObject<HTMLElement | null>;

/**
 * Aceita VÁRIAS refs porque nem todo painel é um bloco só.
 *
 * O menu do celular é o caso: o hambúrguer mora dentro de `.app-nav` e o painel
 * é irmão da barra, não filho. Não existe um ancestral comum que sirva de
 * "dentro" sem englobar a página inteira — então "dentro" é a união das duas
 * caixas.
 */
export const useFecharAoClicarFora = (
  refs: Alvo | Alvo[],
  aberto: boolean,
  fechar: () => void,
): void => {
  const lista = Array.isArray(refs) ? refs : [refs];
  // A lista é recriada a cada render; o que importa para o efeito são as refs
  // em si, e ref é estável. Sem isto o ouvinte se religa a cada render.
  const chave = lista.length;

  useEffect(() => {
    if (!aberto) return;

    const fora = (e: Event) => {
      const alvo = e.target as Node | null;
      if (!alvo) return;
      /*
       * O GATILHO conta como dentro, e tem que contar: ele é um interruptor, e
       * fechar aqui faria o `onClick` dele reabrir na sequência — o painel
       * piscaria e continuaria aberto.
       */
      const dentro = lista.some(r => r.current?.contains(alvo));
      if (!dentro) fechar();
    };

    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, fechar, chave]);
};
